import * as React from 'react';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import { isEqual, compact, isEmpty, entries } from 'lodash';
import { OrderedMap } from 'immutable';
import cx from 'classnames';

import { ActionTypes } from 'groups/constants/GroupConstants';
import { dispatcher } from 'lib/Dispatcher';
// @ts-ignore
import { allTargetsAreGroups } from 'lib/RoleBasedMessagingUtils';
// @ts-ignore
import CharacterCount from 'lib/CharacterCount';
// @ts-ignore
import Composer from 'chat_v2/components/Composer';
// @ts-ignore
import ComposerLinkPreview from 'chat_v2/components/ComposerLinkPreview';
import CurrentUser from 'auth/CurrentUser';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import Message from 'entities/Message';
import ToastActions from 'actions/ToastActions';
import VisualComposerMessageActions from 'chat_v2/actions/VisualComposerMessageActions';
import VisualComposerPrimaryButton from 'chat_v2/components/FullPageComposer/VisualComposerPrimaryButton';

import { Target } from 'entities/Message';
import { ContentSourcesContent_me_composer_contentSourcesContent_items_downloadPreview as DownloadPreview } from 'user/queries/__generated__/ContentSourcesContent';

import 'chat_v2/components/styles/VisualComposer.scss';
import {
  ComposerContext,
  UIEventContext,
} from 'chat_v2/components/SplitComposer/components/ComposerContext';
// @ts-ignore
import ComposerIntegrations from './ComposerIntegrations';
// @ts-ignore
import TranslateButton from 'translations/components/TranslateButton';
import { getLanguagePreviewTitle } from 'translations/utils';
import { TranslationPreview, TranslationState } from 'translations/components/TranslationPreview';
import { Intent, TranslationType, EditedMessageTranslationsType } from 'translations/types';
import VisualComposerScheduler from './VisualComposerScheduler';
import VisualComposerFooter from './VisualComposerFooter';
import { detectLanguage, getLanguageSets, translatedMessage } from 'translations/actions';
import {
  PutMessageInput,
  LanguageSetFiltersInput,
  LanguageSetInput,
  LanguageSetInputTargetType,
  LanguageSetRecipientsRole,
  MessageTranslationSource,
  MessageTranslationIntent,
  PredefinedMessageTranslationsInput,
} from 'globalTypes';
import { MessageFilterType } from 'types/typescript/messagingTypes';
import PAID_ORG_STATUS from 'user/queries/paidOrgStatusQuery.gql';
import { GetLanguageSet_translations_getLanguageSet_data_languageCounts as LanguageCounts } from 'translations/queries/__generated__/GetLanguageSet';
import MetricTracking, { MetricName, MetricTagValue, boolTagValue } from 'lib/MetricTracking';
import { UploadRequestHandler, ThumbnailHandler } from 'lib/fileUploadHelper';
import { ComposerState } from 'chat_v2/ComposerContext';
import { ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity } from 'chat_v2/components/SplitComposer/queries/__generated__/ComposerRecipients';
import {
  putMessage_putMessage_messages_announcements_item,
  putMessage_putMessage_messages_chatMessages_item,
} from 'chat/queries/__generated__/putMessage';

type ComposerLinkPreviewType = {
  title: string;
  description: string;
  url: string;
  image: string;
  image_width: string;
  image_height: string;
  rich_content_type: string;
  rich_content_url: string;
  downloadPreview?: DownloadPreview;
};

type Props = {
  autoFocus?: boolean;
  className?: string | null;
  charLimit: number;
  charLimitWithFiles: number;
  constrainMaxHeight?: boolean;
  districtIsSelected?: boolean;
  draftAnnouncement?: Message;
  onMessageSent: (
    message?:
      | putMessage_putMessage_messages_announcements_item
      | putMessage_putMessage_messages_chatMessages_item
  ) => void;
  onBack: () => void;
  orgUuid?: string;
  orgName?: string;
  userHasPaidOrg: boolean;
  onRequestHide?: () => void;
  onTextChange: (text: string) => void;
  onScheduledTimeChange: (date?: Date | null) => void;
  text: string;
  scheduledTime?: Date;
  uuidForNewMessage?: string;
  linkPreviews?: OrderedMap<string, ComposerLinkPreviewType> | null;
  onRemoveLinkPreview?: (preview: ComposerLinkPreviewType) => void;
  isUrgentMessage?: boolean;
  addLinkedContent: (content: any) => void;
  uploadRequest: UploadRequestHandler;
  loadThumbnail: ThumbnailHandler;
  filePreview?: React.ReactNode;
  fileUuid?: string;
  composerState: ComposerState;
} & ReduxStateProps;

type ReduxStateProps = {
  totalRecipientCount: number;
};

type State = {
  latestTranslation?: string;
  languageCounts: LanguageCounts[];
  shouldTranslate: boolean;
  translationState: TranslationState;
  translationsPreviewActive: boolean;
  translationsPreviewLanguage?: string;
  editedTranslations: EditedMessageTranslationsType;
  detectedLanguageStale: boolean;
  detectedMessageLanguage: string;
};

export class VisualComposer extends React.Component<Props, State> {
  state: State = {
    shouldTranslate: CurrentUser.getUser().getPreference('enabled_composer_translation'),
    translationState: TranslationState.None,
    translationsPreviewActive: false,
    languageCounts: [],
    editedTranslations: {},
    detectedLanguageStale: CurrentUser.getUser().getPreference('enabled_composer_translation'),
    detectedMessageLanguage: 'none',
  };

  async componentDidMount() {
    const { recipients, filters } = this.props.composerState;
    const entities = Object.values(recipients);

    if (!isEmpty(entities)) {
      this.updateLanguageSet(entities, filters);
    }
  }

  async componentDidUpdate(prevProps: Props) {
    const { scheduledTime, onScheduledTimeChange, composerState } = this.props;
    const { recipients, filters } = composerState;
    const entities = Object.values(recipients);

    if (scheduledTime && this.isSchedulingDisabled()) {
      onScheduledTimeChange(null);
    }

    if (
      !isEqual(
        Object.keys(recipients).sort(),
        Object.keys(prevProps.composerState.recipients).sort()
      )
    ) {
      this.updateLanguageSet(entities, filters);
    }
  }

  toggleTranslate = (shouldTranslate: boolean) => {
    this.setState({
      shouldTranslate,
      detectedLanguageStale: shouldTranslate,
      editedTranslations: {},
    });
    CurrentUser.getUser().savePreference('enabled_composer_translation', shouldTranslate);
    this.detectMessageLanguage();
  };

  updateLanguageSet = async (
    entities: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity[],
    filters: { [recipientUuid: string]: { [filterType: string]: string[] } }
  ) => {
    const result = await getLanguageSets(this.languageSetInputs(entities, filters));
    if (result.success) {
      this.setState({
        languageCounts: result.items,
      });
    }
  };

  recipientRoleToEnum = (role: string): LanguageSetRecipientsRole | null => {
    switch (role) {
      case 'admin':
        return LanguageSetRecipientsRole.admin;
      case 'parent':
        return LanguageSetRecipientsRole.parent;
      case 'student':
        return LanguageSetRecipientsRole.student;
      case 'teacher':
        return LanguageSetRecipientsRole.teacher;
      default:
        return null;
    }
  };

  targetFilters = (targetSelectedFilters?: string[]): LanguageSetFiltersInput | null => {
    if (!targetSelectedFilters) {
      return null;
    }
    return {
      roles: compact(targetSelectedFilters.map(this.recipientRoleToEnum)),
    };
  };

  languageSetInputs = (
    entities: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity[],
    filters: { [recipientUuid: string]: { [filterType: string]: string[] } }
  ): LanguageSetInput[] =>
    compact(
      entities.map(entity => {
        switch (entity.recipient.type) {
          case 'district':
            return {
              targetType: LanguageSetInputTargetType.organization,
              targetUuid: entity.recipient.uuid,
              filters:
                !isEmpty(filters) && filters[entity.recipient.uuid]
                  ? this.targetFilters(filters[entity.recipient.uuid]['roles'])
                  : null,
            };
          case 'group':
            return {
              targetType: LanguageSetInputTargetType.group,
              targetUuid: entity.recipient.uuid,
              filters:
                !isEmpty(filters) && filters[entity.recipient.uuid]
                  ? this.targetFilters(filters[entity.recipient.uuid]['roles'])
                  : null,
            };
          case 'group_chat':
            return {
              targetType: LanguageSetInputTargetType.groupChat,
              targetUuid: entity.recipient.uuid,
              filters: null,
            };
          case 'individual':
            return {
              targetType: LanguageSetInputTargetType.individual,
              targetUuid: entity.recipient.uuid,
              filters: null,
            };
          default:
            // We only support the first two types for right now
            return null;
        }
      })
    );

  handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { editedTranslations } = this.state;
    const { onTextChange, text } = this.props;

    // if the message has been cleared completely, clear all the translation data
    if (text.trim() === '') {
      this.setState({
        detectedLanguageStale: true,
        editedTranslations: {},
        translationState: TranslationState.Empty,
      });
    }
    // otherwise, clear the autotranslations, but not the edited ones
    else {
      const stillEditedAfterTextChange: EditedMessageTranslationsType = {};
      Object.keys(editedTranslations)
        .filter(lc => editedTranslations[lc].intent !== 'Autotranslated')
        .forEach(lc => {
          stillEditedAfterTextChange[lc] = editedTranslations[lc];
        });

      this.setState({
        detectedLanguageStale: true,
        editedTranslations: stillEditedAfterTextChange,
      });
    }
    onTextChange(event.currentTarget.value);
  };

  handleMessageSent = (
    message?:
      | putMessage_putMessage_messages_announcements_item
      | putMessage_putMessage_messages_chatMessages_item
  ) => {
    const { onTextChange, onMessageSent } = this.props;
    this.incrementMetricCount('composer.composition.view.sent.success');
    onTextChange('');
    onMessageSent(message);
  };

  handleMessageSentError = (error: Error) => {
    this.incrementMetricCount('composer.composition.view.sent.failed');
    ToastActions.showAPIError(error);
    const dwmPrefix = this.props.districtIsSelected ? 'DWM - ' : '';
    console.warn(error);
    console.error(`${dwmPrefix}VisualComposer sendMessage failed`);
  };

  incrementMetricCount = (name: MetricName) => {
    const submissionMechanism: MetricTagValue = (() => {
      if (this.props.scheduledTime) {
        if (this.props.draftAnnouncement) {
          return 'update_scheduled_message';
        } else {
          return 'new_scheduled_message';
        }
      } else if (
        Object.values(this.props.composerState.recipients).filter(
          entity => entity.recipient.type === 'group_chat'
        ).length > 0
      ) {
        return 'group_chat';
      }

      return 'new_message';
    })();

    MetricTracking.increment(name, {
      tags: {
        is_urgent: boolTagValue(!!this.props.isUrgentMessage),
        has_attachment: boolTagValue(!!this.props.fileUuid),
        has_plt_translations: boolTagValue(!!this.state.latestTranslation),
        is_scheduled: boolTagValue(!!this.props.scheduledTime),
        message_submission_type: submissionMechanism,
      },
    });
  };

  targetLanguageCodes = () =>
    this.state.languageCounts
      .map(languageCount => languageCount.languageCode)
      .filter(code => code !== 'none');

  getMessageTranslationIntent = (intent: Intent): MessageTranslationIntent => {
    switch (intent) {
      case 'Autotranslated':
        return MessageTranslationIntent.auto;
      case 'Edited':
        return MessageTranslationIntent.edited;
      default:
        return MessageTranslationIntent.off;
    }
  };

  getPredefinedTranslations = (): PredefinedMessageTranslationsInput[] => {
    const { editedTranslations } = this.state;

    return entries(editedTranslations)
      .filter(([_lc, { intent }]) => intent != 'Autotranslated')
      .map(([languageCode, { intent, userEditedTranslation }]) => {
        const translationIntent: MessageTranslationIntent = this.getMessageTranslationIntent(
          intent
        );
        return {
          languageCode,
          translationIntent,
          translationSource:
            translationIntent == MessageTranslationIntent.off
              ? MessageTranslationSource.off
              : MessageTranslationSource.custom,
          translatedText: userEditedTranslation || '',
        };
      });
  };

  putMessageInputFromState(senderType: string, senderUuid: string): PutMessageInput {
    const {
      text,
      fileUuid,
      uuidForNewMessage,
      linkPreviews,
      isUrgentMessage,
      composerState,
    } = this.props;

    const { recipients, filters } = composerState;
    const { editedTranslations, detectedLanguageStale, detectedMessageLanguage } = this.state;

    const recipientsInput = Object.values(recipients).map(entity => ({
      type: entity.recipient.type,
      uuid: entity.recipient.uuid,
      filters: Object.keys(filters[entity.recipient.uuid] || {}).map(filterType => ({
        [filterType]: filters[entity.recipient.uuid][filterType],
      })),
    }));

    const previews = linkPreviews ? linkPreviews.toList().toArray() : [];

    const fileUuids = [];
    if (fileUuid) {
      fileUuids.push(fileUuid);
    }

    return {
      recipients: recipientsInput,
      message: {
        body: text,
        fileUuids,
        linkPreviews: previews.map(preview => {
          return {
            url: preview.url,
            title: preview.title,
            description: preview.description,
            image: preview.image,
            image_width: preview.image_width ? String(preview.image_width) : null,
            image_height: preview.image_height ? String(preview.image_height) : null,
            rich_content_type: preview.rich_content_type,
            rich_content_url: preview.rich_content_url,
          };
        }),
        urgent: isUrgentMessage || false,
        translations: this.state.shouldTranslate
          ? {
              targetLanguageCodes: this.targetLanguageCodes(),
              predefinedTranslations: isEmpty(this.state.editedTranslations)
                ? null
                : this.getPredefinedTranslations(),
              originalLanguageCode:
                !detectedLanguageStale && detectedMessageLanguage != 'none'
                  ? detectedMessageLanguage
                  : null,
            }
          : null,
      },
      uuid: uuidForNewMessage,
      sender: {
        type: senderType,
        uuid: senderUuid,
      },
    };
  }

  sendMessage = (propagateThrow: boolean = false): Promise<void> => {
    const {
      scheduledTime,
      uuidForNewMessage,
      draftAnnouncement,
      districtIsSelected,
      orgUuid,
    } = this.props;

    const putMessageInput = this.putMessageInputFromState('user', CurrentUser.getUserUuid());
    if (scheduledTime) {
      if (draftAnnouncement) {
        return VisualComposerMessageActions.updateScheduledMessage({
          uuid: uuidForNewMessage ?? '',
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: (scheduledTime as unknown) as string,
          },
        })
          .then(response => {
            // Remove the original message in our store because send-to-few creates a new message
            dispatcher.dispatch({
              type: ActionTypes.DESTROY_GROUP_SCHEDULED_MESSAGE,
              message: draftAnnouncement,
            });
            this.handleMessageSent(response.messages?.announcements[0]?.item);
          }, this.handleMessageSentError)
          .catch(err => {
            errorLogger('VisualComposer sendMessage failed')(err);
            return propagateThrow ? Promise.reject(err) : undefined;
          });
      } else {
        return VisualComposerMessageActions.scheduleMessage({
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: (scheduledTime as unknown) as string,
          },
        })
          .then(
            response => this.handleMessageSent(response.messages?.announcements[0]?.item),
            this.handleMessageSentError
          )
          .catch(err => {
            errorLogger('VisualComposer sendMessage failed')(err);
            return propagateThrow ? Promise.reject(err) : undefined;
          });
      }
    } else if (districtIsSelected) {
      if (!orgUuid) {
        // just making sure this never causes unhandled promises.
        return propagateThrow ? Promise.reject() : Promise.resolve();
      }
      const districtPutMessageInput = this.putMessageInputFromState('district', orgUuid);
      return VisualComposerMessageActions.putMessage({ input: districtPutMessageInput })
        .then(
          response =>
            this.handleMessageSent(
              response.messages?.announcements[0]?.item || response.messages?.chatMessages[0]?.item
            ),
          this.handleMessageSentError
        )
        .catch(err => {
          errorLogger('DWM - VisualComposer sendMessage promise failed')(err);
          return propagateThrow ? Promise.reject(err) : undefined;
        });
    } else {
      return VisualComposerMessageActions.putMessage({ input: putMessageInput })
        .then(
          response =>
            this.handleMessageSent(
              response.messages?.announcements[0]?.item || response.messages?.chatMessages[0]?.item
            ),
          this.handleMessageSentError
        )
        .catch(err => {
          errorLogger('VisualComposer sendMessage failed')(err);
          return propagateThrow ? Promise.reject(err) : undefined;
        });
    }
  };

  isValidMessage = () => {
    const { filePreview, fileUuid, composerState, text } = this.props;
    const { recipients } = composerState;

    const isAddressed = Object.keys(recipients).length > 0;

    const uploadComplete = filePreview ? !!fileUuid : true;

    const hasText = text.trim().length > 0;

    return isAddressed && uploadComplete && (hasText || !!fileUuid);
  };

  isSchedulingDisabled = () => {
    const { districtIsSelected, composerState } = this.props;
    const allTargetsAreGroups = Object.values(composerState.recipients).every(
      entity => entity.recipient.type === 'group'
    );
    return districtIsSelected || !allTargetsAreGroups;
  };

  userHasPreferredLanguageV2Feature = () =>
    CurrentUser.getUser().hasFeature('2021.aug.preferred_language_translation_v2');

  detectMessageLanguage = () => {
    if (!this.userHasPreferredLanguageV2Feature()) {
      return;
    }

    const { detectedLanguageStale } = this.state;
    const { text } = this.props;

    if (text.trim() === '') {
      this.updateDetectedMessageLanguage('none');
      return;
    }

    if (detectedLanguageStale) {
      detectLanguage(text).then(resp => {
        if (resp && resp.success) {
          this.updateDetectedMessageLanguage(resp.languageCode);
        }
      });
    }
  };

  noPreferenceComposerText = (noPreferenceCount: number) => {
    return (
      noPreferenceCount +
      (noPreferenceCount > 1 ? " recipients haven't" : " recipient hasn't") +
      " set a preferred language and will receive the original message. To change someone's preferred language, edit their profile from the people list."
    );
  };

  getCurrentTextTranslation = (languageCode?: string) => {
    const { text } = this.props;
    const { languageCounts, editedTranslations } = this.state;
    if (!languageCode) {
      return null;
    }
    if (languageCode === 'none') {
      const noPreference = languageCounts.filter(lc => lc.languageCode === 'none')[0];
      return this.noPreferenceComposerText(noPreference.count);
    }
    const translationData = editedTranslations[languageCode];
    if (!translationData) {
      return null;
    }
    switch (translationData.intent) {
      case 'Autotranslated':
        return translationData.autoTranslation;

      case 'Edited':
        return translationData.userEditedTranslation || '';

      case 'Off':
        return text;

      default:
        return null;
    }
  };

  getCharactersRemaining = () => {
    const { charLimit, charLimitWithFiles, text, fileUuid } = this.props;
    return CharacterCount.getRemainingCharactersForText(
      charLimit,
      charLimitWithFiles,
      text.trim(),
      !!fileUuid
    );
  };

  updateDetectedMessageLanguage = (languageCode?: string | null | undefined) => {
    if (!languageCode) return;

    this.setState({
      detectedMessageLanguage: languageCode,
      detectedLanguageStale: false,
    });
  };

  retrieveAndPreviewAutotranslation = (languageCode: string) => {
    const { text } = this.props;
    const { detectedLanguageStale, detectedMessageLanguage, editedTranslations } = this.state;
    const { userEditedTranslation } = editedTranslations[languageCode] || {};

    const updateForTranslationState = (translationState: TranslationState) => {
      this.setState({
        translationState,
        translationsPreviewActive: true,
        translationsPreviewLanguage: languageCode,
      });
    };

    if (this.getCharactersRemaining() < 0) {
      updateForTranslationState(TranslationState.LimitError);
      return;
    }

    if (text.trim() === '') {
      updateForTranslationState(TranslationState.Empty);
      return;
    } else {
      updateForTranslationState(TranslationState.Loading);
    }

    const translatedMessageResponse = translatedMessage({
      languageCode: detectedLanguageStale ? 'none' : detectedMessageLanguage,
      targetLanguageCode: languageCode,
      text: text,
    });
    translatedMessageResponse
      .then(response => {
        if (response.success) {
          this.saveUpdatedTranslation(languageCode, {
            intent: 'Autotranslated',
            autoTranslation: response.translatedText,
            userEditedTranslation,
            originalText: text,
          });
          this.updateDetectedMessageLanguage(response.detectedLanguageCode);
          updateForTranslationState(TranslationState.Success);
        } else {
          updateForTranslationState(TranslationState.ServerError);
        }
      })
      .catch(error => {
        errorLogger(error);
        updateForTranslationState(TranslationState.ServerError);
      });
  };

  displayTranslatedMessagePreview = (languageCode: string) => {
    const { languageCounts, editedTranslations } = this.state;
    let translationData = editedTranslations[languageCode];

    // don't even bother apollo if a preference isn't set
    if (languageCode === 'none') {
      const noPreference = languageCounts.filter(lc => lc.languageCode === 'none')[0];
      translationData = {
        intent: 'Off',
        autoTranslation: this.noPreferenceComposerText(noPreference.count),
        originalText: this.props.text,
      } as TranslationType;
      this.saveUpdatedTranslation('none', translationData);
    }

    // if we have already done a translation for this, use that
    if (translationData) {
      this.setState({
        translationsPreviewActive: true,
        translationsPreviewLanguage: languageCode,
        translationState: translationData.originalText.trim() === '' ? TranslationState.Empty : TranslationState.Success,
      });
      return;
    }

    this.retrieveAndPreviewAutotranslation(languageCode);
  };

  backToOriginal = () => {
    this.setState({
      translationsPreviewActive: false,
    });
  };

  saveUpdatedTranslation = (languageCode: string, translation: TranslationType) => {
    const editedTranslationsState = this.state.editedTranslations;
    editedTranslationsState[languageCode] = translation as TranslationType;

    this.setState({
      editedTranslations: editedTranslationsState,
    });
  };

  renderTranslateButton(uiEventContext: UIEventContext) {
    const { text, onTextChange, composerState } = this.props;
    const {
      shouldTranslate,
      languageCounts,
      translationsPreviewActive,
      editedTranslations,
      detectedMessageLanguage,
    } = this.state;
    const { recipients } = composerState;

    return (
      <TranslateButton
        shouldTranslate={shouldTranslate}
        languageCounts={languageCounts}
        toggleTranslate={this.toggleTranslate}
        onTextChange={onTextChange}
        onLangChange={(lang: string) => this.setState({ latestTranslation: lang })}
        eventKey="announcements.announcement_modal.universal_composer.show_translation_box.click"
        eventProperties={uiEventContext}
        dataTestSelector="visual_composer_translation_button"
        textToTranslate={text}
        translationsPreviewActive={translationsPreviewActive}
        displayTranslatedMessagePreview={this.displayTranslatedMessagePreview}
        backToOriginalCallback={this.backToOriginal}
        isDisabled={isEmpty(Object.keys(recipients))}
        editedTranslations={editedTranslations}
        detectedMessageLanguage={detectedMessageLanguage}
      />
    );
  }

  renderLinkPreviews(linkPreviews: OrderedMap<string, ComposerLinkPreviewType>) {
    const { onRemoveLinkPreview } = this.props;
    const items = linkPreviews.valueSeq().map(preview => {
      if (!preview) {
        return;
      }

      return (
        <ComposerLinkPreview
          key={preview.url}
          linkPreview={preview}
          onRemove={() => {
            if (onRemoveLinkPreview) {
              onRemoveLinkPreview(preview);
            }
          }}
        />
      );
    });
    return <div className="VisualComposer-contentPreviewItems">{items}</div>;
  }

  render() {
    const {
      charLimit,
      charLimitWithFiles,
      constrainMaxHeight,
      autoFocus,
      text,
      linkPreviews,
      districtIsSelected,
      isUrgentMessage,
      orgUuid,
      onRequestHide,
      totalRecipientCount,
      orgName,
      addLinkedContent,
      scheduledTime,
      onScheduledTimeChange,
      uploadRequest,
      loadThumbnail,
      filePreview,
      fileUuid,
      userHasPaidOrg,
      composerState,
    } = this.props;
    const {
      latestTranslation,
      translationsPreviewActive,
      translationsPreviewLanguage,
      editedTranslations,
      translationState,
    } = this.state;

    const charactersRemaining = this.getCharactersRemaining();

    const linkedContentSection =
      linkPreviews && linkPreviews.size > 0 ? this.renderLinkPreviews(linkPreviews) : null;

    let previewTranslation: TranslationType | undefined;
    if (translationsPreviewLanguage) {
      previewTranslation = editedTranslations[translationsPreviewLanguage] || {
        intent: 'Autotranslated',
        originalText: text,
      };
    }

    const composerValue = translationsPreviewActive
      ? this.getCurrentTextTranslation(translationsPreviewLanguage)
      : text;

    return (
      <ComposerContext.Consumer>
        {composerContext => (
          <div
            className={cx('VisualComposer', this.props.className)}
            data-test-selector="visual_composer_textarea">
            <div className="VisualComposer-composerWrapper">
              <div className="VisualComposer-wrap">
                <div className="VisualComposer-composerContainer">
                  {translationsPreviewActive &&
                  previewTranslation &&
                  translationsPreviewLanguage ? (
                    <TranslationPreview
                      canEdit={userHasPaidOrg && this.userHasPreferredLanguageV2Feature()}
                      filePreview={filePreview}
                      onClose={this.backToOriginal}
                      languageCode={translationsPreviewLanguage}
                      textBeforeTranslation={text}
                      currentTranslation={previewTranslation}
                      translationState={translationState}
                      handleTranslationChange={this.saveUpdatedTranslation}
                      getUpdatedTranslation={this.retrieveAndPreviewAutotranslation}
                    />
                  ) : (
                    <Composer
                      className="VisualComposer"
                      placeholder="Type your message"
                      autogrow
                      constrainMaxHeight={constrainMaxHeight}
                      value={composerValue}
                      disabled={translationsPreviewActive}
                      autoFocus={autoFocus}
                      error={charactersRemaining < 0}
                      showTitle={translationsPreviewActive}
                      title={getLanguagePreviewTitle(translationsPreviewLanguage || '')}
                      hideTitleAction={this.backToOriginal}
                      onChange={this.handleTextChange}
                      onBlur={this.detectMessageLanguage}>
                      <div className="VisualComposer-filePreview">{filePreview}</div>
                      {linkedContentSection}
                    </Composer>
                  )}
                </div>
                <VisualComposerFooter
                  className="VisualComposer-buttonContainer"
                  scheduleButton={
                    !translationsPreviewActive ? (
                      <VisualComposerScheduler
                        scheduledTime={scheduledTime}
                        disabled={this.isSchedulingDisabled()}
                        onSetScheduledTime={onScheduledTimeChange}
                      />
                    ) : (
                      undefined
                    )
                  }
                  attachButton={
                    !translationsPreviewActive ? (
                      <ComposerIntegrations
                        addLinkedContent={addLinkedContent}
                        hasFile={!!filePreview}
                        hasLinks={linkPreviews?.size}
                        uploadRequest={uploadRequest}
                        loadThumbnail={loadThumbnail}
                      />
                    ) : (
                      undefined
                    )
                  }
                  sendButton={
                    !translationsPreviewActive ? (
                      <VisualComposerPrimaryButton
                        charactersRemaining={charactersRemaining}
                        districtIsSelected={districtIsSelected}
                        isScheduling={!!scheduledTime}
                        isUrgentMessage={isUrgentMessage}
                        latestTranslation={latestTranslation}
                        messageIsValid={this.isValidMessage()}
                        uiEventContext={composerContext.getUIEventContext()}
                        onContinueConfirm={() => {
                          this.sendMessage(true)
                            .then(() => {
                              EventTracking.trackKey(
                                'announcements.composer.confirm_send.send.click',
                                {
                                  ...composerContext.getUIEventContext(),
                                  organization_uuid: orgUuid,
                                }
                              );
                            })
                            .catch(() => {
                              console.error(
                                `${
                                  isUrgentMessage ? 'URGENT' : 'DWM'
                                } - missing district uuid - could not write district message`
                              );
                            });
                          if (onRequestHide) {
                            onRequestHide();
                          }
                        }}
                        onSchedule={() => this.sendMessage(false)}
                        onSend={() => this.sendMessage(false)}
                        orgName={orgName}
                        orgUuid={orgUuid}
                        totalRecipientCount={totalRecipientCount}
                        composerState={composerState}
                      />
                    ) : (
                      undefined
                    )
                  }
                  translateButton={this.renderTranslateButton(composerContext.getUIEventContext())}
                  charactersRemaining={charactersRemaining}
                />
              </div>
            </div>
          </div>
        )}
      </ComposerContext.Consumer>
    );
  }
}

const mapStateToProps = (state: any): ReduxStateProps => {
  return {
    totalRecipientCount: state.roleBasedMessagingSearch.totalRecipientCount,
  };
};

const withPaidStatus = graphql(PAID_ORG_STATUS, {
  props: ({ data }) => {
    const foundPaidOrg = (userData: any) => {
      return (
        !userData?.me?.organizations?.every((o: any) => !o.paid) ||
        !userData?.me?.classes?.every((c: any) => !c.organization || !c.organization.paid)
      );
    };
    const hasPaidOrg = foundPaidOrg(data);

    return { userHasPaidOrg: hasPaidOrg };
  },
});

export default compose(connect(mapStateToProps), withPaidStatus)(VisualComposer);
